import { BCMSRecord, DisabledPrompt, useBCMSContext, useModel, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import EDEStatementsPageContainer from "components/EDEStatements/shared/containers/EDEStatementsPageContainer";
import { CenterContentContainer, ContentHeaderView } from 'components/shared';
import Classes from "model";
import React, { useEffect, useRef, useState } from "react";
import { ImageAsset } from "res";
import "./styles.css";
import "./mobile.css";
import { PrimaryButton, SecondaryButton } from "components/ui";
import EDEStatementsEnrollmentPageContentView from "components/EDEStatements/shared/views/EDEStatementsEnrollmentPageContentView";
import { UITextInput } from "components/ui";
/**
 * @function EDEStatementsEnrollmentAgreementPage
 * @return {React.Component}
 * @exported true
 * @description The "EDE Statements Enrollment Agreement" page.
 */
export default function EDEStatementsEnrollmentAgreementPage(props) {
  // Initalize all model classes
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  // Sets the values for the data and ui variables
  const data = { ...getDefaultPageData(), ...props.data ?? {} };
  const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
  // The state to be passed to the state manager
  const state = { data, ui };
  const navigate = useRoute();
  const { nextStep } = useWorkflow() ?? {};
  
  // :: React.state, React.setState -> OBJECT
  // Function to initalize the controller for the state manager
  const controller = (state, setState) => ({
      data: state.data, // Set the data property of the controller
      ui: state.ui, // Set the ui property of the controller
      onAgreeClick: evt => {
        nextStep();
      }
  });
  
  return(
    <EDEStatementsPageContainer className={ `ede-statements-enrollment-agreement-page` } state={ state } controller={ controller }>
        <PageContent/>
    </EDEStatementsPageContainer>
  );
}

/**
 * @name getDefaultPageData
 * @param {Void} 
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = () => {
  return {
    confirmationCode: { value: "" }
  }
};

/**
* @name getDefaultPageUI
* @param {Void} 
* @return {OBJECT}
* @description Hook to return the default ui required by the page.
*/
export const getDefaultPageUI = (props) => {
  let scrollBarIsActive = false;

  if(props) {
    scrollBarIsActive = props.scrollBarIsActive === "true" || props.scrollBarIsActive === true;
  }

  return {
    scrollBar: { isActive: scrollBarIsActive }
  };
};

/**
 * @function PageContent
 * @return {React.Component}
 * @exported false
 * @description The content for this page.
 */
function PageContent(props) {
  const { data, ui, onAgreeClick } = useStateManager();
  const agreementTextContainerRef = useRef();
  const [ didScroll, setDidScroll ] = useState(false);
  const { settings } = useModel();
  let additionalText = [];

  // Create additional lorem ipsum text so that the scroll content looks larger.
  for(let i = 0; i < 10; i++) {
    additionalText.push(
      <p key={ i }>
        Lorem ipsum dolor sit amet, consectetur adipiscing
        elit. Sed non risus. Suspendisse lectus tortor, dignissim sit amet,
        adipiscing nec, ultricies sed, dolor. Cras elementum ultrices diam.
        Maecenas ligula massa, varius a, semper congue, euismod non, mi. Proin
        porttitor, orci nec nonummy molestie, enim est eleifend mi, non fermentum
        diam nisl sit amet erat. Duis semper. Duis arcu massa, scelerisque vitae,
        consequat in, pretium a, enim. Pellentesque congue. Ut in risus volutpat
        libero pharetra tempor. Cras vestibulum bibendum augue. Praesent egestas
        leo in pede. Praesent blandit odio eu enim. Pellentesque sed dui ut augue
        blandit sodales. Vestibulum ante ipsum primis in faucibus orci luctus et
        ultrices posuere cubilia Curae; Aliquam nibh. Mauris ac mauris sed pede
        pellentesque fermentum. Maecenas adipiscing ante non diam sodales
        hendrerit.
      </p>
    );
  }

  useEffect(() => {
    // This code mimics a scroll in order to show the scrollbar.
    // It is used for video captures only.
    if(ui.scrollBar.isActive && !didScroll) {
        var count = 0;
        var scrollInterval = setInterval(function() {
            count += 1;
            agreementTextContainerRef.current?.scrollBy({
                top: 1,
                behavior: "auto",
            });
            agreementTextContainerRef.current?.scrollBy({
                top: -1,
                behavior: "auto",
            });

            if(count == 100) {
                clearInterval(scrollInterval);
            }
      }, 100);

      setDidScroll(true);
    }
  });

  return (
      <EDEStatementsEnrollmentPageContentView>
        <CenterContentContainer>
            <ContentHeaderView text="Online Statement and Notice Enrollment - Agreement" />
            <p>
              <span>{ `Welcome to ${ settings.fiName } Online Statement and Notice Service.` }</span>
            </p>
            <p>
              <span>You must accept this agreement in order to view your statements and notices through this web site.</span>
            </p>
            <span className="print-link-container">
              <DisabledPrompt>
                <a className="clickable link">
                  <span>Print</span>
                </a>
              </DisabledPrompt>
            </span>
            
            <div className="agreement-text-container">
              <div ref={ agreementTextContainerRef } className="agreement-text">
                <p className="agrement-title-label">
                  <span>{ `Bank of ${ settings.fiName } eStatement and eNotice Enrollment Terms and Conditions` }</span>
                </p>
                <p className="agreement-details-1">
                  <span>
                    By accepting the terms below, you agree to receive your selected periodic
                    account statement(s) electronically via Online Banking and you agree that
                    you will no longer receive your statements in paper form. It also means that
                    you agree to receive online any legal notices and other information affecting
                    your account(s) that would otherwise have been included with your paper
                    statement, including, but not limited to, account agreements, fee schedules,
                    privacy and other regulatory notices, and amendments to any of those
                    documents. Other electronic disclosures may include:
                  </span>
                </p>
                <ul className="agreement-details-list-1">
                  <li>Monthly Banking Agreement and any amendments thereto</li>
                  <li>Monthly account statements</li>
                  <li>Deposit account disclosures</li>
                  <li>Lending disclosures</li>
                  <li>Privacy Policy</li>
                  <li>Change-In-Terms Notices</li>
                </ul>
                { additionalText }
                {/* This is a fake scrollbar for the video captures only. */}
                {/*<div class="fake-scrollbar">
                    <div class="fake-scrollbar-thumb"></div>
                </div>*/}
              </div>
            </div>
            <div className="flex-container flex-horizontal adobe-acrobat-reader-icon-container">
              <div className="flex-width"></div>
              <div className="set-width">
                <DisabledPrompt>
                  <ImageAsset id="AdobeAcrobatReaderPromoIcon" className={ "clickable" } />
                </DisabledPrompt>
              </div>
            </div>
            <div className="verification-code-instructions-container">
              <span>
                <span>{ `Click ` }</span>
                <DisabledPrompt>
                  <a className="here-label clickable">
                    <span data-point="EDE_Statements_Enrollment_Agreement_Here_Link">{ `HERE` }</span>
                  </a>
                </DisabledPrompt>
                <span>{ ` to get a verification code then enter the code in the box below.` }</span>
              </span>
            </div>

            <div>
              <div>
                <span>{ `eSign Confirmation Code` }</span>
              </div>
              <UITextInput value={ `${ data.confirmationCode.value }` } dataPoint="EDE_Statements_Enrollment_Agreement_Confirmation_Code_Input" />
            </div>
            <div className="flex-container flex-horizontal button-container">
              <div className="set-width primary-button-container">
                <PrimaryButton text="I AGREE" onClick={ onAgreeClick } dataPoint="EDE_Statements_Enrollment_Agreement_Agree_Button"></PrimaryButton>
              </div>
              <DisabledPrompt>
                <div className="set-width">
                  <SecondaryButton text="I DISAGREE" dataPoint="EDE_Statements_Enrollment_Agreement_Disagree_Button"></SecondaryButton>
                </div>
              </DisabledPrompt>
            </div>
        </CenterContentContainer>
      </EDEStatementsEnrollmentPageContentView>
  );
}
